import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, UserPlus, Loader2, CheckCircle2, User, Crown } from "lucide-react";
import { toast } from "sonner";
import { fuzzySearchPatients } from "@/services/smartfile";
import { ensurePatientByCPF } from "@/services/miafile.dal";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { supabase } from "@/integrations/supabase/client";

interface PatientLinkerProps {
  suggestedName?: string | null;
  suggestedCPF?: string | null;
  onPatientLinked: (patientId: string) => void;
  isChangingPatient?: boolean; // Novo prop para indicar troca de paciente
}

export function PatientLinker({ suggestedName, suggestedCPF, onPatientLinked, isChangingPatient }: PatientLinkerProps) {
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [searchQuery, setSearchQuery] = useState("");
  const [patients, setPatients] = useState<Array<{
    id: string;
    full_name: string;
    cpf: string;
    similarity?: number;
    matchType?: string;
  }>>([]);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");

  const subscriptionStatus = useSubscriptionStatus();
  const { patientsCount } = useSubscriptionStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (suggestedName || suggestedCPF) {
      const query = suggestedCPF || suggestedName || "";
      setSearchQuery(query);
      performSearch(query);
    }
  }, [suggestedName, suggestedCPF]);

  useEffect(() => {
    if (mode === 'create') {
      if (suggestedName) setName(suggestedName);
      if (suggestedCPF) setCpf(suggestedCPF);
    }
  }, [mode, suggestedName, suggestedCPF]);


  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setPatients([]);
      return;
    }

    console.log('[PatientLinker] 🔎 Iniciando busca:', {
      query,
      suggestedCPF,
      timestamp: new Date().toISOString()
    });

    setBusy(true);
    try {
      const results = await fuzzySearchPatients(
        query,
        suggestedCPF,
        0.3 // threshold: 30% similaridade mínima
      );

      console.log('[PatientLinker] ✅ Resultados recebidos:', {
        count: results.length,
        results: results.map(r => ({
          name: r.full_name,
          matchType: r.matchType,
          similarity: r.similarity
        }))
      });

      setPatients(results);

      if (results.length === 0) {
        toast.info("Nenhum paciente encontrado. Tente criar um novo.");
      } else if (results[0].matchType === 'fuzzy_match' && (results[0].similarity || 0) < 0.6) {
        toast.warning(
          `${results.length} resultado(s) aproximado(s). Verifique se é o paciente correto.`
        );
      } else {
        toast.success(`${results.length} paciente(s) encontrado(s)!`);
      }
    } catch (error: any) {
      console.error('[PatientLinker] ❌ Erro ao buscar:', error);
      toast.error("Erro ao buscar pacientes: " + error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSearch = () => {
    performSearch(searchQuery);
  };

  const handleSelectPatient = (patientId: string) => {
    toast.success("Paciente vinculado!");
    onPatientLinked(patientId);
  };

  const handleCreatePatient = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome do paciente");
      return;
    }

    // Validar CPF obrigatório
    if (!cpf || !cpf.trim()) {
      toast.error("CPF é obrigatório");
      return;
    }

    // Verificar limite do plano antes de criar
    if (!subscriptionStatus.canAddPatients) {
      setShowUpgradeModal(true);
      return;
    }

    setBusy(true);
    try {
      const patient = await ensurePatientByCPF({
        full_name: name.trim(),
        cpf: cpf.trim(),
      });

      toast.success("Paciente criado e vinculado!");
      onPatientLinked(patient.id);
    } catch (error: any) {
      toast.error(error.message || "Erro ao criar paciente");
      console.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-secondary">
      <CardHeader>
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-secondary" />
          <CardTitle>Vincular Paciente</CardTitle>
        </div>
        <CardDescription>
          {isChangingPatient ? (
            "Selecione um novo paciente ou crie um cadastro"
          ) : suggestedName || suggestedCPF ? (
            <span className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                Dados sugeridos pelo documento
              </Badge>
            </span>
          ) : (
            "Busque um paciente existente ou crie um novo cadastro"
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant={mode === 'search' ? 'default' : 'outline'}
            onClick={() => setMode('search')}
            className="flex-1"
          >
            <Search className="h-4 w-4 mr-2" />
            Buscar Existente
          </Button>
          <Button
            variant={mode === 'create' ? 'default' : 'outline'}
            onClick={() => setMode('create')}
            className="flex-1"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Criar Novo
          </Button>
        </div>

        {mode === 'search' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                className="w-full"
                placeholder="Buscar por nome ou CPF..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} disabled={busy} className="w-full sm:w-auto">
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {patients.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {patients.map((patient) => (
                  <Card key={patient.id} className="p-3 hover:bg-secondary/10 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{patient.full_name}</p>
                          {patient.matchType === 'fuzzy_match' && patient.similarity && (
                            <Badge variant="outline" className="text-xs">
                              {(patient.similarity * 100).toFixed(0)}% similar
                            </Badge>
                          )}
                          {patient.matchType === 'exact_cpf' && (
                            <Badge className="text-xs bg-green-500">
                              CPF Match
                            </Badge>
                          )}
                        </div>
                        {patient.cpf && (
                          <p className="text-sm text-muted-foreground">CPF: {patient.cpf}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSelectPatient(patient.id)}
                        disabled={busy}
                        className="w-full sm:w-auto bg-secondary hover:bg-secondary/90"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Vincular
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === 'create' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="patient-name">Nome Completo *</Label>
              <Input
                id="patient-name"
                placeholder="Nome completo do paciente"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="patient-cpf">CPF *</Label>
              <Input
                id="patient-cpf"
                placeholder="CPF (somente números)"
                value={cpf}
                onChange={(e) => setCpf(e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleCreatePatient}
                disabled={busy}
                className="bg-secondary hover:bg-secondary/90"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Criar e Vincular
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Modal de Limite Atingido */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason="patients"
        currentCount={patientsCount}
      />
    </Card>
  );
}
